import React, { useEffect } from 'react';
import { RouterProvider } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import routes from './routes/routes';
import { LoaderProvider } from './loader/LoaderContext';
import Loader from './loader/Loader';
import { ToastContainer } from 'react-toastify';
import 'react-loading-skeleton/dist/skeleton.css';

import { login, initializeAuth } from './reducers/authSlice';
import { initializeAuthStore } from './utils/authUtils';
import { StripeProvider } from './contexts/StripeContext';
import { CoverageVerificationProvider, useCoverageVerification } from './contexts/CoverageVerificationContext';
import InitLoader from './components/InitLoader/InitLoader';
import awsconfig from './awsconfig';
import { Amplify } from 'aws-amplify';

Amplify.configure({ ...awsconfig });

/**
 * Generate or retrieve session ID for analytics tracking
 * Format: session_YYYY-MM-DD_randomString
 * Regenerates daily to provide accurate DAU metrics
 */
const getOrCreateSessionId = () => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const sessionPrefix = `session_${today}_`;

  // Check sessionStorage for existing session matching today's date
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(sessionPrefix)) {
      return key;
    }
  }

  // No session for today found, create new one
  const randomString = Math.random().toString(36).substring(2, 10);
  const newSessionId = `${sessionPrefix}${randomString}`;
  sessionStorage.setItem(newSessionId, Date.now().toString());

  return newSessionId;
};

/**
 * AppContent component - handles auth monitoring and logout cleanup
 */
function AppContent() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { clearCoverageState } = useCoverageVerification();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const authData = await initializeAuthStore();

        if (authData) {
          dispatch(login(authData));
        } else {
          dispatch(initializeAuth());
        }
      } catch (err) {
        console.error('Error initializing auth store:', err);
      }
    };

    initAuth();
  }, [dispatch]);

  // Initialize or refresh session ID daily
  useEffect(() => {
    const sessionId = getOrCreateSessionId();
    console.log('[App] Session initialized:', sessionId);
    
    // Store in window for easy access throughout app
    window.currentSessionId = sessionId;
  }, []); // Run once on mount

  // Clear coverage state when user logs out
  useEffect(() => {
    if (isAuthenticated === false) {
      console.log('[App] User logged out, clearing coverage state');
      clearCoverageState();
    }
  }, [isAuthenticated, clearCoverageState]);

  if (Object.is(isAuthenticated, null)) {
    return <InitLoader />;
  }

  return (
    <>
      <RouterProvider router={routes}></RouterProvider>
      <Loader />
      <ToastContainer />
    </>
  );
}

/**
 * Main App component - wraps AppContent with providers
 */
function App() {
  return (
    <LoaderProvider>
      <StripeProvider>
        <CoverageVerificationProvider>
          <AppContent />
        </CoverageVerificationProvider>
      </StripeProvider>
    </LoaderProvider>
  );
}

export default App;
